import { addDays } from 'date-fns';
import { expandOccurrences, formatLocalDate, parseLocalDate, type Recurrence, type Task, type TaskCompletion } from '@app/shared';
import { useRecords } from '../../platform/data/repo.ts';

export function useTasks(): Task[] | undefined {
  return useRecords<Task>('tasks');
}

export function useCompletions(): TaskCompletion[] | undefined {
  return useRecords<TaskCompletion>('task_completions');
}

export interface TaskState {
  task: Task;
  /** Occurrence key to complete next: "once" or a date. */
  occurrence: string;
  dueDate: string | null;
  overdue: boolean;
  /** Completed today (shown checked until tomorrow). */
  doneToday: TaskCompletion | null;
  /** One-off task that is finished. */
  finished: boolean;
  history: TaskCompletion[];
}

/**
 * Where a chore stands today. Repeating chores have one "current"
 * occurrence: the next one after the last completion. If several were
 * missed, they collapse into a single overdue item instead of piling up.
 */
export function taskState(task: Task, completions: TaskCompletion[], today = formatLocalDate(new Date())): TaskState {
  const history = completions.filter((c) => c.taskId === task.id).sort((a, b) => b.completedAt.localeCompare(a.completedAt));
  const doneToday = history.find((c) => c.completedAt.slice(0, 10) === today || formatLocalDate(new Date(c.completedAt)) === today) ?? null;
  if (!task.recurrence) {
    const done = history.find((c) => c.occurrence === 'once') ?? null;
    return {
      task,
      occurrence: 'once',
      dueDate: task.dueDate,
      overdue: !done && task.dueDate !== null && task.dueDate < today,
      doneToday: done && doneToday ? done : null,
      finished: Boolean(done),
      history,
    };
  }
  const start = parseLocalDate(task.dueDate ?? formatLocalDate(new Date(task.createdAt)));
  const doneDates = history
    .map((c) => c.occurrence)
    .filter((o) => o !== 'once')
    .sort();
  const lastDone = doneDates[doneDates.length - 1];
  const from = lastDone ? addDays(parseLocalDate(lastDone), 1) : start;
  const todayDate = parseLocalDate(today);
  const candidates = expandOccurrences(start, task.recurrence as Recurrence, from < start ? start : from, addDays(todayDate, 400), { limit: 400 }).map(
    formatLocalDate,
  );
  const first = candidates[0] ?? null;
  if (first === null) return { task, occurrence: lastDone ?? 'once', dueDate: null, overdue: false, doneToday, finished: true, history };
  if (first < today) {
    const latestPast = candidates.filter((c) => c <= today).pop() ?? first;
    return { task, occurrence: latestPast, dueDate: latestPast, overdue: latestPast < today, doneToday, finished: false, history };
  }
  return { task, occurrence: first, dueDate: first, overdue: false, doneToday, finished: false, history };
}

export const REMINDER_OPTIONS = [
  { value: '', label: 'No reminder' },
  { value: '0', label: 'At the due time' },
  { value: '15', label: '15 minutes before' },
  { value: '60', label: '1 hour before' },
  { value: '1440', label: '1 day before' },
];
